import { useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { format0 } from 'utils/number';
import type { PreDrillWorkspace } from 'types/api-responses';
import { refreshGeometryAtom } from './atoms';

export const TARGET_TVD_MAX_FT = 25000;

export interface GeometryRefreshOptions {
  /** Rebuild an untouched filed survey (UAT 25), which the backend otherwise keeps. */
  rebuildSurvey?: boolean;
}

export interface GeometryDepthControls {
  /** Following the confirmed zone's grid (a grid exists and the operator has not opted out). */
  gridDataActive: boolean;
  /** Whether a grid exists at the well to follow at all. */
  hasUsableGrid: boolean;
  setUseGridData: (checked: boolean) => void;
  /** The Target TVD field as typed, with thousands separators; '' when blank. */
  tvdInput: string;
  setTvdInput: (raw: string) => void;
  tvdNum: number | null;
  tvdOutOfRange: boolean;
  /** Staged inputs differ from what is built. */
  depthDirty: boolean;
  /** The staged inputs can be committed. */
  depthValid: boolean;
  /** A refresh was just enqueued and no input has changed since. */
  recalcSubmitted: boolean;
  setRecalcSubmitted: (value: boolean) => void;
  refreshEnabled: boolean;
  refreshing: boolean;
  /** Enqueue the geometry refresh with the staged depth choice. */
  refresh: (options?: GeometryRefreshOptions) => void;
}

interface Options {
  workspace: PreDrillWorkspace;
  /** The bore's refresh address under the Well Workspace; omitted, the geometry
   *  workspace's own endpoint. */
  refreshHref?: string | null;
  /** Permitted vertical depth — the default the field pre-fills when generalizing. */
  permittedDepthTvdFt: number | null;
  /** Called once the refresh is enqueued, so the host re-reads the show (arms the push). */
  onRefreshEnqueued: () => void;
}

/**
 * The depth controls shared by the Subsurface Explorer and the Wellbore Points panel:
 * follow the confirmed zone's grid or a typed Target TVD (blank → the permit's), and
 * enqueue the refresh. One hook so the two surfaces never disagree about what's staged.
 * @param {Options} options The workspace, the permit fallback and the enqueue callback.
 * @return {GeometryDepthControls} The staged inputs, their validity and the refresh.
 */
export const useGeometryDepthControls = ({
  workspace,
  refreshHref,
  permittedDepthTvdFt,
  onRefreshEnqueued,
}: Options): GeometryDepthControls => {
  const [refreshState, refreshGeometry] = useAtom(refreshGeometryAtom);

  // "Use Grid Data": checked → follows the structure grid; unchecked → generalized
  // via the entered Target TVD (blank → permit fallback).
  const hasUsableGrid = !workspace.noGeologicReference;
  const committedTvd = workspace.targetZoneGeneralizedTvdFt ?? null;
  const committedUseGridData = workspace.targetZoneUseGridData;
  // Field default when generalizing with no committed depth: the permitted TVD (the
  // BE fallback), so the field shows the built value, not an empty box.
  const defaultTvd = committedTvd ?? permittedDepthTvdFt;
  const [useGridData, setUseGridDataState] = useState(committedUseGridData);
  const [tvdInput, setTvdInputState] = useState(format0(defaultTvd, ''));
  // A refresh the user just triggered, held disabled from click until the next edit
  // (a rebuild's transient geometryStale must not re-arm it).
  const [recalcSubmitted, setRecalcSubmitted] = useState(false);
  // Whether the operator has touched either control since the last commit. The permit
  // depth arrives on a separate, later read, and re-seeding then would wipe what they
  // just typed.
  const touched = useRef(false);

  // Resync staged inputs to the committed values whenever the show reports a new
  // commit (e.g. after a refresh completes) so the fields mirror what's built.
  useEffect(() => {
    if (touched.current) return;
    setUseGridDataState(committedUseGridData);
    setTvdInputState(format0(defaultTvd, ''));
  }, [committedUseGridData, defaultTvd]);
  // A new commit supersedes whatever was staged, so the fields follow it again.
  useEffect(() => {
    touched.current = false;
  }, [committedUseGridData, committedTvd]);

  const gridDataActive = hasUsableGrid && useGridData;
  const tvdNum =
    tvdInput.trim() === '' ? null : Number(tvdInput.replace(/[^0-9.]/g, ''));
  const tvdOutOfRange =
    tvdNum !== null && (tvdNum < 0 || tvdNum > TARGET_TVD_MAX_FT);
  const depthValid = gridDataActive || !tvdOutOfRange;
  // The field holds whole feet, so compare against the committed value rounded the
  // same way — a fractional permit depth must not read as a staged change.
  const defaultTvdWhole = defaultTvd === null ? null : Math.round(defaultTvd);
  const depthDirty =
    gridDataActive !== committedUseGridData ||
    (!gridDataActive && tvdNum !== defaultTvdWhole);

  const setUseGridData = (checked: boolean) => {
    touched.current = true;
    setUseGridDataState(checked);
    // A fresh edit re-arms the refresh (supersedes a submitted one).
    setRecalcSubmitted(false);
  };

  const setTvdInput = (raw: string) => {
    // Keep digits only, then re-apply thousand separators so the field reads e.g.
    // "12,500"; out-of-range is flagged, not clamped.
    touched.current = true;
    const digits = raw.replace(/[^0-9]/g, '');
    setTvdInputState(digits === '' ? '' : format0(Number(digits)));
    setRecalcSubmitted(false);
  };

  const refreshEnabled =
    workspace.resources.geometryRefreshes.auth.includes('create');

  const refresh = ({ rebuildSurvey = false }: GeometryRefreshOptions = {}) => {
    setRecalcSubmitted(true);
    refreshGeometry({
      type: 'POST',
      url: refreshHref ?? workspace.resources.geometryRefreshes.href,
      // `gridDataActive` (not the checkbox) is authoritative; generalizing sends the
      // TVD (null → permit).
      data: {
        geometry_refresh: {
          use_grid_data: gridDataActive,
          generalized_tvd_ft: gridDataActive ? null : tvdNum,
          ...(rebuildSurvey ? { rebuild_survey: true } : {}),
        },
      },
      onSuccess: () => onRefreshEnqueued(),
    });
  };

  return {
    gridDataActive,
    hasUsableGrid,
    setUseGridData,
    tvdInput,
    setTvdInput,
    tvdNum,
    tvdOutOfRange,
    depthDirty,
    depthValid,
    recalcSubmitted,
    setRecalcSubmitted,
    refreshEnabled,
    refreshing: refreshState.loading,
    refresh,
  };
};
